import { Box, Typography } from '@mui/material';
import {
  Clock,
  DownloadSimple,
  FilePlus,
  Paperclip,
  Trash,
} from 'phosphor-react';
import { convertDate } from './TaskList';
import { useAppSelector } from '../../store/hooks';
import axios from 'axios';
import { useModal } from '../../context/ModalProvider/useModal';
import { UploadDocumentModal } from './UploadDocumentModal';

type DocumentsComponentProps = {
  requiredDocuments: any[];
  onAddFile: (file: File, reqDocumentId: string) => void;
  onDelete: (id: string) => void;
};

const DocumentsComponent = ({
  requiredDocuments,
  onAddFile,
  onDelete,
}: DocumentsComponentProps) => {
  const { openModal } = useModal();
  const authUser = useAppSelector(state => state.user.user);

  const handleUploadModal = (reqDocumentId: any) => {
    openModal({
      title: 'Upload Document',
      body: <UploadDocumentModal />,
      onSubmit: (file: File) => onAddFile(file, reqDocumentId),
    });
  };

  const onDownload = (src: string) => {
    const url = new URL(src!);
    const file = url.pathname.split('/').pop();
    const filename = file!.split('.')[0];

    const link = document.createElement('a');

    axios
      .get(src!, {
        responseType: 'blob',
      })
      .then(res => {
        link.href = URL.createObjectURL(res.data);
        link.download = filename;
        link.click();
      })
      .finally(() => {
        link.remove();
      });
  };

  return (
    <Box
      sx={{
        marginTop: '32px',
      }}
    >
      <Typography
        sx={{
          marginTop: '16px',
          marginBottom: '8px',
        }}
        fontSize={'18px'}
        lineHeight={'22px'}
        fontWeight={'600'}
        color={'#202A2D'}
      >
        Documents
      </Typography>
      {requiredDocuments
        .filter(reqDoc => !(authUser?.role !== 'user' && !reqDoc.document))
        .map(reqDocument => {
          const document = reqDocument.document;

          if (!!document) {
            return (
              <Box
                key={document.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                  border: '1px solid #E3DDE6',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  padding: '21px 16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    maxWidth: '400px',
                    width: '100%',
                  }}
                >
                  <Paperclip size={20} color={'#202A2D'} />
                  <Typography
                    color={'#202A2D'}
                    fontSize={'14px'}
                    fontWeight={'500'}
                    lineHeight={'19px'}
                  >
                    {document.title}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10.5px',
                  }}
                >
                  <Clock size={20} color='#655C68' />
                  <Typography
                    color={'#655C68'}
                    fontSize={'14px'}
                    fontWeight={'400'}
                    lineHeight={'19px'}
                  >
                    Uploaded at {convertDate(document.updatedAt)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}
                >
                  <DownloadSimple
                    onClick={() => onDownload(document.src)}
                    cursor={'pointer'}
                    size={20}
                    color='#655C68'
                  />
                  {authUser?.role === 'user' && (
                    <Trash
                      size={20}
                      cursor={'pointer'}
                      onClick={() => onDelete(document.id)}
                      color='#655C68'
                    />
                  )}
                </Box>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px dashed #3293DB',
                borderRadius: '4px',
                marginBottom: '16px',
                padding: '16px',
              }}
              key={reqDocument.id}
              onClick={() => handleUploadModal(reqDocument.id)}
            >
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <FilePlus size={20} color='#3293DB' cursor={'pointer'} />
                <Typography
                  color={'#3293DB'}
                  fontSize={'16px'}
                  fontWeight={'500'}
                  lineHeight={'19px'}
                >
                  {reqDocument.name}
                </Typography>
              </Box>

              <Typography
                color={'#3293DB'}
                fontSize={'14px'}
                fontWeight={'400'}
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => handleUploadModal(reqDocument.id)}
                lineHeight={'19px'}
              >
                Click to upload the document
              </Typography>
            </Box>
          );
        })}
    </Box>
  );
};

export default DocumentsComponent;
